import { createSelector } from '@reduxjs/toolkit';
import { selectSellerId } from '../auth/authSelectors';
import { buildAvailableEcommerces } from './ecommercesConstants';
import { getPlatformAvailability, getPlatformSlug } from './ecommercesPlatforms';

const selectLogos = (state) => state.ecommerces.logos;
const selectIntegrations = (state) => state.ecommerces.integrations;
const selectWarehouses = (state) => state.ecommerces.warehouses;

export const selectEcommerceFilters = (state) => state.ecommerces.filters;

/**
 * The catalog of integrations the seller can add. Derived rather than stored:
 * it is the literal templates plus the seller id and the logos.
 */
export const selectAvailableEcommerces = createSelector(
  [selectSellerId, selectLogos],
  (sellerId, logos) => buildAvailableEcommerces({ sellerId, logos })
);

/** Warehouse names by id, as strings: the listing stores the id as a number. */
export const selectWarehouseNames = createSelector([selectWarehouses], (warehouses) =>
  Object.fromEntries(warehouses.map((warehouse) => [String(warehouse.id), warehouse.name]))
);

/**
 * The listing after the filters. Everything happens here: the backend answers
 * the whole list, unpaginated.
 *
 * The search looks at the platform, the warehouse name and the shop the
 * integration points to, where the platform has one.
 */
export const selectFilteredIntegrations = createSelector(
  [selectIntegrations, selectWarehouseNames, selectEcommerceFilters],
  (integrations, warehouseNames, { search, platform, warehouse }) => {
    const term = search.trim().toLowerCase();

    return integrations.filter((integration) => {
      if (platform && getPlatformSlug(integration.name) !== platform) return false;
      if (warehouse && String(integration.warehouse) !== String(warehouse)) return false;
      if (!term) return true;

      const authentication = integration.configuration?.authentication || {};
      const haystack = [
        integration.name,
        warehouseNames[String(integration.warehouse)],
        authentication.shop_url,
        authentication.shop_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });
  }
);

/**
 * @param {number|string} id - From the address, so possibly a string.
 */
export const selectIntegrationById = (id) => (state) =>
  state.ecommerces.integrations.find((integration) => String(integration.id) === String(id)) ||
  null;

/** See `getPlatformAvailability`. */
export const selectPlatformAvailability = createSelector(
  [selectIntegrations, selectWarehouses],
  (integrations, warehouses) => getPlatformAvailability({ integrations, warehouses })
);
